<template>
  <div class="side-menu-container" ref="sideMenuContainerRef">
    <div
      ref="sideMenuRef"
      class="side-menu"
      :class="{
        expanded: isOpen,
        'variant-expanded': true,
      }"
      @click.stop
    >
      <!-- Logo Header -->
      <slot name="toggle">
        <LogoHeaderMenu :is-expanded="isOpen" />
      </slot>

      <!-- Menu Header -->
      <div class="menu-header">
        <slot name="header">
          <BurgerMenuButton :is-open="isOpen" @toggle="handleToggle" />
        </slot>
      </div>

      <component :is="menuContentComponent" :is-expanded="!!isOpen" />

      <!-- Footer Section -->
      <div v-if="$slots.footer" class="menu-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { useUIStore } from '@/common/stores/uiStore';
import { useLayout } from '@/layouts/composables/useLayout';
import { USER_TYPE } from '@/common/constants';
import BurgerMenuButton from '@/common/components/ui/button/BurgerMenuButton.vue';
import LogoHeaderMenu from '@/layouts/navigation-layout/desktop/LogoHeaderMenu.vue';
import { MenuContentStudent, MenuContentEmployee } from '@/layouts/components/menu-content';

// Mapping des composants de menu par type utilisateur (USER_TYPE)
const MENU_CONTENT_COMPONENTS: Record<string, Component> = {
  [USER_TYPE.STUDENT]: MenuContentStudent,
  [USER_TYPE.COLLABORATEUR]: MenuContentEmployee,
  [USER_TYPE.ADMINISTRATOR]: MenuContentEmployee,
};

// Stores
const uiStore = useUIStore();

// État d'ouverture: source unique de vérité depuis le store
const isOpen = computed(() => uiStore.isDrawerOpen);

// Composables
const { userType } = useLayout();

const menuContentComponent = computed<Component>(() => {
  const type = userType.value ?? USER_TYPE.STUDENT;
  return MENU_CONTENT_COMPONENTS[type] ?? MenuContentStudent;
});

// Actions - Le store est la source unique de vérité
function handleToggle() {
  uiStore.toggleDrawer();
}
</script>

<style scoped>
.side-menu-container {
  position: fixed;
  top: 10px;
  height: 98%;
  left: 0;
  z-index: 1000;
  display: flex;
  margin-left: 10px;
}

.side-menu {
  display: flex;
  flex-direction: column;
  background: var(--ion-background-color);
  border-radius: 3vmin;
  overflow: visible;
  padding: 20px 0;
  width: 60px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--ion-input-border-color);
  border-left: none;
  height: 100%;
}

.side-menu.expanded {
  width: 18rem;
}

/* Variants */
.side-menu.variant-compact {
  width: 60px;
}

.side-menu.variant-compact.expanded {
  width: 15rem;
}

.side-menu.variant-minimal {
  width: 50px;
  padding: 10px 0;
}

.side-menu.variant-minimal.expanded {
  width: 12rem;
}

.menu-header {
  position: fixed;
  top: 0;
  left: 80px;
  padding: 10px 0;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
}

.side-menu.expanded ~ .menu-header,
.side-menu-container:has(.side-menu.expanded) .menu-header {
  left: 15rem;
  top: 10px;
}

.menu-routes {
  flex-shrink: 0;
}

.menu-footer {
  padding: 10px;
  border-top: 1px solid var(--ion-border-color);
  flex-shrink: 0;
}
</style>
